import { IPromise, IQService } from 'angular';
import { BehaviorSubject } from 'rxjs';
import {
    IDjangoOrder,
    IDjangoOrderSummary,
    IDjangoPayment,
    IOrder,
    IOrderSummary,
    IPayment,
    IStripePaymentProviderData,
    OrderRepository,
} from '..';
import DjangoClient from '../../tn.django/django-client.service/djangoClient';
import {
    FONT_RIGHT_KEYS,
    IFontStyleModel,
    IFoundryModel,
    IOrderLicense,
} from '../classes';
import {
    GA4Item,
    GA4PurchaseEventParams,
    GtmService,
} from '../classes/gtm/GtmService';
import { FoundryService } from './FoundryService';
import { IDjangoLicense, hasLicenseType } from '../classes/Model/Licenses';

export const LICENSE_TYPES = [
    'desktop',
    'web',
    'app',
    'epub',
    'broadcast',
    'server',
    'game',
    'embedding',
] as const;
export type LicenseType = typeof LICENSE_TYPES[number];

export interface IOrderFiltersDate {
    year?: string;
    month?: string;
}

export interface IOrderFilters {
    startDate?: Date;
    endDate?: Date;
    costMin?: number;
    costMax?: number;
    licensees?: Record<string, boolean>;
    licenseTypes?: Record<string, boolean>;
}

// converts and IOrderFilterDate to a date time and passes it to the predicate
export function filterOrdersByDate(
    orders: IOrderSummary[],
    date: IOrderFiltersDate,
    predicate: (order: IOrderSummary, date: Date) => boolean
): IOrderSummary[] {
    if (!date) return orders;
    if (!date.year) return orders;
    if (!date.month) return orders;
    const year = parseInt(date.year);
    const month = parseInt(date.month) - 1;
    const filterDate = new Date(year, month);
    return orders.filter((order) => predicate(order, filterDate));
}

export function filterOrdersByStartDate(
    orders: IOrderSummary[],
    filters: IOrderFilters
): IOrderSummary[] {
    if (!filters.startDate) return orders;
    const { startDate } = filters;
    return orders.filter((order) => order.created >= startDate);
}

export function filterOrdersByEndDate(
    orders: IOrderSummary[],
    filters: IOrderFilters
): IOrderSummary[] {
    if (!filters.endDate) return orders;
    const { endDate } = filters;
    return orders.filter((order) => order.created <= endDate);
}

export function filterOrdersByMinCost(
    orders: IOrderSummary[],
    filters: IOrderFilters
): IOrderSummary[] {
    if (!filters.costMin) return orders;
    const min = filters.costMin;
    return orders.filter((order) => {
        return order.total >= min;
    });
}

export function filterOrdersByMaxCost(
    orders: IOrderSummary[],
    filters: IOrderFilters
): IOrderSummary[] {
    if (!filters.costMax) return orders;
    const max = filters.costMax;
    return orders.filter((order) => {
        return order.total <= max;
    });
}

export function filterOrdersByLicensee(
    orders: IOrderSummary[],
    filters: IOrderFilters
): IOrderSummary[] {
    if (!filters.licensees) return orders;
    const licensees = filters.licensees;
    if (Object.keys(licensees).length == 0) return orders;

    return orders.filter((order) => {
        return licensees[order.licensee];
    });
}

export function filterOrdersByLicenseType(
    orders: IOrder[],
    filters: IOrderFilters
): IOrder[] {
    if (!filters.licenseTypes) return orders;
    const licenseTypes = Object.keys(filters.licenseTypes);
    // dont filter if all possible options are selected.
    if (licenseTypes.length == LICENSE_TYPES.length) return orders;
    return orders.filter((order) =>
        licenseTypes.some((type) => orderHasLicenseType(order, type))
    );
}

export function orderHasLicenseType(order: IOrder, type: LicenseType): boolean {
    return order.licenses.some((license) => hasLicenseType(license, type));
}

export function filterOrderSummaries(
    filters: IOrderFilters,
    orders: IOrderSummary[]
): IOrderSummary[] {
    if (!filters) return orders;
    const startDateFiltered = filterOrdersByStartDate(orders, filters);
    const endDateFiltered = filterOrdersByEndDate(startDateFiltered, filters);
    const minCostFiltered = filterOrdersByMinCost(endDateFiltered, filters);
    const maxCostFiltered = filterOrdersByMaxCost(minCostFiltered, filters);
    const licenseeFiltered = filterOrdersByLicensee(maxCostFiltered, filters);
    return licenseeFiltered;
}

export function filterOrders(
    filters: IOrderFilters,
    orders: IOrder[]
): IOrder[] {
    if (!filters) return orders;
    const summaryFiltered = filterOrderSummaries(filters, orders) as IOrder[];
    const licenseTypeFiltered = filterOrdersByLicenseType(
        summaryFiltered,
        filters
    );
    return licenseTypeFiltered;
}

/**
 *
 */
export class OrderFilters extends BehaviorSubject<IOrderFilters> {
    constructor() {
        super({});
    }
}

export function transformDjangoPaymentToPayment(
    payment: IDjangoPayment
): IPayment {
    const created = new Date(payment.created);
    let provider_data = {} as IStripePaymentProviderData;

    // provider_data is sometimes not valid json, in that case return an empty object
    if (typeof payment.provider_data === 'object') {
        provider_data = payment.provider_data as IStripePaymentProviderData;
    } else if (typeof payment.provider_data === 'string') {
        try {
            provider_data = JSON.parse(
                payment.provider_data
            ) as IStripePaymentProviderData;
        } catch (e) {
            // suppress error
        }
    }

    return Object.assign({}, payment, { created, provider_data });
}

export function getOrderPaymentType(
    order: Pick<IDjangoOrderSummary, 'status'>,
    payments: IPayment[]
): string {
    if (order.status === 3) {
        return 'Cancelled';
    }
    const hasPayments = payments.length > 0;
    if (!hasPayments) return '';
    if (payments[0].provider === 1) {
        return order.status === 2 ? 'Paid, Invoiced' : 'Unpaid, Invoiced';
    }
    if (payments[0].provider === 0) {
        if (!payments[0].provider_data) return '';
        const providerData = payments[0].provider_data;
        if (providerData.free_trial) return 'Free Trial';
        if (providerData.free_order) return 'Free';
        if (providerData.card) return providerData.card.brand;
        if (providerData.message) return providerData.message;
        return 'Unknown';
    }
    return '';
}

export function transformDjangoOrderSummaryToOrderSummary(
    order: IDjangoOrderSummary
): IOrderSummary {
    const created = new Date(order.created);
    const licensee =
        `${order.licensee_first_name} ${order.licensee_last_name}`.trim();
    const payments = order.payments.map(transformDjangoPaymentToPayment);
    const paymentType = getOrderPaymentType(order, payments);
    const total = parseFloat(order.total);
    return Object.assign({}, order, {
        created,
        licensee,
        payments,
        paymentType,
        total,
    });
}

export function transformDjangoOrderToOrder(order: IDjangoOrder): IOrder {
    const created = new Date(order.created);
    const licensee =
        order.licensee_company ||
        `${order.licensee_first_name} ${order.licensee_last_name}`;
    const payments = order.payments.map(transformDjangoPaymentToPayment);
    const paymentType = getOrderPaymentType(order, payments);
    const total = parseFloat(order.total);
    return Object.assign({}, order, { created, licensee, paymentType, payments, total });
}

export function transformIFontRightToGA4Item(
    foundryIdx: Record<number, IFoundryModel>,
    style: IFontStyleModel,
    right: IDjangoLicense,
    coupon?: string
): GA4Item {
    const hosting =
        right.license_type == 'web'
            ? right.self_hosted
                ? 'self hosted'
                : 'hosted'
            : '';
    const units =
        right.license_type === 'desktop'
            ? 'users'
            : right.license_type == 'app'
                ? 'apps'
                : right.license_type == 'epub'
                    ? 'publcations'
                    : 'views/month';
    const item = {
        item_id: style.id.toString(),
        item_name: style.family_name,
        item_variant: style.style_name,
        item_brand: foundryIdx[style.canonical_foundry_id].name,
        item_category: right.license_type + (hosting ? ' ' + hosting : ''),
        item_category2: right.years ? `${right.years} years` : 'perpetual',
        item_category3: `${right.qty} ${units}`,
        price: parseFloat(right.price),
        quantity: 1,
        coupon,
    };
    return item;
}

export function transformILicenseToGA4Items(
    foundryIdx: Record<number, IFoundryModel>,
    license: IOrderLicense,
    coupon?: string
): GA4Item[] {
    const products: GA4Item[] = [];
    FONT_RIGHT_KEYS.forEach((key: LicenseType) => {
        const _license = license.fontRights[key];
        if (!_license) return;
        const product = transformIFontRightToGA4Item(
            foundryIdx,
            license.style,
            _license,
            coupon
        );
        products.push(product);
    });
    return products;
}

function transformIOrderToGA4Items(
    order: IOrder,
    foundryIdx: Record<number, IFoundryModel>
): GA4Item[] {
    const products: GA4Item[] = [];
    order.licenses.forEach((license) => {
        products.push(
            ...transformILicenseToGA4Items(
                foundryIdx,
                license,
                order.coupon || undefined
            )
        );
    });
    return products;
}

export class OrderService {
    protected summaryPromise?: IPromise<IOrderSummary[]>;
    protected orderCache: Record<number, IPromise<IOrder>> =
        Object.create(null);

    static $inject = [
        '$q',
        'djangoClient',
        'orderRepository',
        'gtmService',
        'foundryService',
    ];
    constructor(
        protected $q: IQService,
        protected djangoClient: DjangoClient,
        protected orderRepository: OrderRepository,
        protected gtm: GtmService,
        protected foundryService: FoundryService
    ) {}

    placeOrder(cart): IPromise<IOrder> {
        const placeOrderPromise = this.djangoClient
            .post<IDjangoOrder>('/api/1/orders/', cart)
            .then((result) => {
                const order = transformDjangoOrderToOrder(result);
                this.orderCache[order.id] = this.$q.when(order);
                // reload the order summaries
                if (this.summaryPromise) delete this.summaryPromise;
                return order;
            });

        placeOrderPromise.then((order) => {
            this.foundryService.getDjangoFoundries().then(() => {
                const foundryIdx =
                    this.foundryService.djangoFoundriesIdxByDjanoId;

                // GA4 Purchase
                const transactionId: string = order.id.toString();
                const affiliation: string | undefined = undefined;
                const revenue: number | undefined = order.total;
                const tax: number | undefined = parseFloat(order.tax) || 0;
                const shipping: number | undefined = 0;
                const coupon: string | undefined = order.coupon || undefined;

                const ga4PurchaseEventParams: GA4PurchaseEventParams = {
                    currency: 'USD',
                    transaction_id: transactionId,
                    value: revenue,
                    items: transformIOrderToGA4Items(order, foundryIdx),
                    affiliation,
                    coupon,
                    shipping,
                    tax,
                };
                this.gtm.purchase(ga4PurchaseEventParams);
            });
        });

        return placeOrderPromise;
    }

    getSummaryOrders(
        filters: IOrderFilters,
        refresh = false
    ): IPromise<IOrderSummary[]> {
        if (!this.summaryPromise || refresh) {
            this.summaryPromise = this.orderRepository
                .getAll()
                .then((orders) => {
                    return orders.map(
                        transformDjangoOrderSummaryToOrderSummary
                    );
                });
        }
        return this.summaryPromise.then((orders) => {
            return filterOrderSummaries(filters, orders);
        });
    }

    getOrder(id: number): IPromise<IOrder> {
        if (id in this.orderCache) {
            return this.orderCache[id];
        }
        const promise = this.orderRepository
            .get(id)
            .then(transformDjangoOrderToOrder);
        this.orderCache[id] = promise;
        return promise;
    }
}
